// Live doc pane: renders the core doc state (state.doc — persisted with the
// session) as the full Marqo template, word-diffing each change. Added words
// render green with a green gutter mark; removals leave a red gutter mark;
// every subsequent change dims all older marks, so brightness reads as
// recency at a glance.

import { DOC_SECTIONS } from "../core/doc";
import { completeEmptyDocSections, docFilledCount, docSectionText, foldDecisionIntoDoc, isDocFolded } from "../core/docflow";
import { state } from "../core/state";
import { diffWords, type DiffOp } from "../lib/diff";
import { storage } from "../lib/storage";
import { $, el } from "./dom";
import { toast } from "./toast";

const LD = {
  seq: 0,                    // global change counter (drives ageing)
  chain: Promise.resolve(),  // decisions route serially, in order
  queued: new Set<string>(),
};

// opacity/colour by age: newest = full, each later change dims a step
function ageAlpha(age: number): number { return Math.max(0.15, 1 - (LD.seq - age) * 0.22); }

function refreshAges(): void {
  document.querySelectorAll<HTMLElement>("#ldBody [data-age]").forEach((n) => {
    const a = ageAlpha(+n.dataset.age!);
    if (n.classList.contains("ld-add")) {
      // added text: fades from green back to normal text colour
      n.style.color = a > 0.3 ? `rgba(94, 222, 138, ${0.45 + 0.55 * a})` : "";
      n.style.fontWeight = a > 0.5 ? "600" : "";
    } else {
      n.style.opacity = String(a);   // gutter marks just dim
    }
  });
}

// The pane always shows every template section, in template order, so the doc
// reads as a document being filled in — not a pile of fragments.
function renderDocSkeleton(): void {
  const body = $("#ldBody");
  body.innerHTML = "";
  DOC_SECTIONS.forEach((name) => {
    const sec = el("div", "ld-sec");
    sec.dataset.sec = name;
    sec.appendChild(el("div", "ld-h", name));
    const lines = el("div", "ld-lines");
    lines.appendChild(el("div", "ld-empty", "—"));
    sec.appendChild(lines);
    body.appendChild(sec);
  });
}

function renderSection(section: string, ops: DiffOp[]): void {
  const sec = [...$("#ldBody").children].find((c) => (c as HTMLElement).dataset.sec === section) as HTMLElement | undefined;
  if (!sec) return;
  const lines = sec.querySelector(".ld-lines")!;
  lines.innerHTML = "";
  let line: HTMLElement | null = null, txt: HTMLElement | null = null, hasAdd = false, hasDel = false;
  const flush = () => {
    if (!line) return;
    const gut = el("span", "ld-gut" + (hasAdd ? " g-add" : "") + (hasDel ? " g-del" : ""));
    if (hasAdd || hasDel) gut.dataset.age = String(LD.seq);
    line.prepend(gut);
    lines.appendChild(line);
    line = null; txt = null; hasAdd = false; hasDel = false;
  };
  const ensure = (): HTMLElement => {
    if (!line) { line = el("div", "ld-line"); txt = el("span", "ld-txt"); line.appendChild(txt); }
    return txt!;   // tokens flow inline inside one wrapper, not as flex items
  };
  for (const op of ops) {
    if (op.t === "del") { ensure(); hasDel = true; continue; }   // removal = mark only
    const parts = op.s.split("\n");
    parts.forEach((part, k) => {
      if (k > 0) flush();
      if (!part) return;
      const target = ensure();
      if (op.t === "add") {
        const sp = el("span", "ld-add", part);
        sp.dataset.age = String(LD.seq);
        target.appendChild(sp);
        hasAdd = true;
      } else {
        target.appendChild(document.createTextNode(part));
      }
    });
  }
  flush();
}

async function processDecision(nodeId: string): Promise<void> {
  LD.queued.delete(nodeId);
  const node = state.nodes.get(nodeId);
  if (!node || node.chosenIndex == null) return;
  try {
    const applied = await foldDecisionIntoDoc(node);
    if (!applied.length) return;
    LD.seq++;                       // one decision = one age step
    applied.forEach((u) => renderSection(u.section, diffWords(u.prev, u.next)));
    refreshAges();
    updateFillMeter();
  } catch { /* advisory pane — stay quiet */ }
}

export function enqueueDecision(nodeId: string): void {
  if (!nodeId || isDocFolded(nodeId) || LD.queued.has(nodeId)) return;
  if (document.body.classList.contains("ld-collapsed")) return;
  LD.queued.add(nodeId);
  LD.chain = LD.chain.then(() => processDecision(nodeId)).catch(() => { });
}

export function resetLiveDoc(): void {
  LD.queued.clear();
  LD.seq = 0;
  LD.chain = Promise.resolve();
  renderDocSkeleton();
  updateFillMeter();
}

/**
 * Render persisted sections instantly (no model calls), then fold in only the
 * decisions that were never routed (e.g. made while the pane was collapsed).
 */
export function primeLiveDoc(): void {
  LD.queued.clear();
  LD.seq = 0;
  LD.chain = Promise.resolve();
  renderDocSkeleton();
  for (const s of DOC_SECTIONS) {
    const text = docSectionText(s);
    if (text) renderSection(s, [{ t: "keep", s: text }]);
  }
  updateFillMeter();
  [...state.nodes.values()]
    .filter((n) => n.depth > 0 && n.chosenIndex != null && !isDocFolded(n.id))
    .sort((a, b) => (parseInt(a.id.slice(1), 10) || 0) - (parseInt(b.id.slice(1), 10) || 0))
    .forEach((n) => enqueueDecision(n.id));
}

function updateFillMeter(): void {
  const m = document.getElementById("ldFill");
  if (m) m.textContent = `${docFilledCount()}/${DOC_SECTIONS.length}`;
}

/** The ✎ button: sweep the trace into every still-empty section. */
let completing = false;
async function runDocCompletion(): Promise<void> {
  if (completing) return;
  const empty = DOC_SECTIONS.length - docFilledCount();
  if (!empty) { toast("every section already has content"); return; }
  completing = true;
  const btn = document.getElementById("ldComplete") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "…"; }
  toast(`completing ${empty} empty section${empty > 1 ? "s" : ""} from the trace…`);
  const applied = await completeEmptyDocSections((u) => {
    LD.seq++;
    renderSection(u.section, diffWords(u.prev, u.next));
    refreshAges();
    updateFillMeter();
  });
  if (btn) { btn.disabled = false; btn.textContent = "✎ complete"; }
  completing = false;
  toast(applied.length ? `filled ${applied.length} section${applied.length > 1 ? "s" : ""} ✓` : "nothing more the trace can fill — decide more first");
}

export function initLiveDoc(): void {
  renderDocSkeleton();   // the empty template is the starting state
  updateFillMeter();
  const collapsed = storage.get("lattice.ld") === "min";
  if (collapsed) document.body.classList.add("ld-collapsed");
  $("#ldToggle").onclick = () => {
    const min = document.body.classList.toggle("ld-collapsed");
    storage.set("lattice.ld", min ? "min" : "open");
    if (!min) primeLiveDoc();   // catch up on what happened while collapsed
  };
  const complete = document.getElementById("ldComplete");
  if (complete) complete.onclick = () => void runDocCompletion();
}
